import { useEffect, useRef } from 'react';
import { logout, startLogin } from '@/api/auth';

/**
 * /signin renders nothing of its own: it sends the browser straight to the Melio accounts
 * create-account screen rather than showing a portal sign-in page and waiting for a click. The
 * redirect goes through the backend's `/api/auth/login?screen=signup`, so PKCE + state and the OAuth
 * params (`client_id`, `redirect_uri`, `scope`, `state`, `resource`, `code_challenge`) are set
 * server-side and carried into accounts' create-account `redirect` param — see `buildSignUpUrl`.
 * `returnTo` rides along too.
 *
 * A callback error (auth-service or the backend bounced back to `/signin?error=…`) takes the very
 * same path, with one step in front of it: the accounts SSO session is dropped first. Whatever went
 * wrong, the account is treated as simply not signed in, and the user starts over on the accounts
 * screens where another address can be used — an account the portal's allowlist refuses
 * (apps/api/src/lib/emailAllowlist.ts) being the case this matters most for. Signing out is also
 * what keeps it from looping: authorize would otherwise re-issue a code for the same session and
 * land the browser right back here, with nothing on screen and no user input needed.
 *
 * The trade-off is deliberate: no error is reported in the portal any more, so a deterministic
 * failure (a refused account, a broken token exchange) re-prompts at accounts on every attempt
 * rather than explaining itself. The code stays in the URL as `?error=` and the backend logs the
 * cause.
 */
export default function SignInRoute() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const returnTo = params.get('returnTo') ?? undefined;

  // Leave exactly once; StrictMode re-runs effects in dev and both of these navigate asynchronously.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // logout() ends the accounts SSO session and returns here without the `error` param, which then
    // starts the sign-in afresh.
    if (error) void logout();
    else startLogin({ screen: 'signup', returnTo });
  }, [error, returnTo]);

  return null;
}
